'use client';

import Image from 'next/image';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';

type InAppNotification = {
  id: string;
  template: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
  is_read: boolean;
  read_at: string | null;
};

function coerceNotifications(payload: unknown): InAppNotification[] {
  // DRF may return a paginated payload: { results: [...] }.
  const data = payload as any;
  const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];

  return list
    .filter((item: any) => item && (typeof item.id === 'string' || typeof item.id === 'number'))
    .map((item: any) => ({
      id: String(item.id),
      template: typeof item.template === 'string' ? item.template : '',
      subject: typeof item.subject === 'string' ? item.subject : '',
      message: typeof item.message === 'string' ? item.message : '',
      status: typeof item.status === 'string' ? item.status : '',
      created_at: typeof item.created_at === 'string' ? item.created_at : '',
      is_read: Boolean(item.is_read),
      read_at: typeof item.read_at === 'string' ? item.read_at : null,
    }));
}

async function fetchJson(path: string, init?: RequestInit) {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      'Content-Type': 'application/json',
    },
  });

  const text = await res.text();
  let parsed: any = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const message =
      typeof parsed?.detail === 'string'
        ? parsed.detail
        : typeof parsed?.message === 'string'
          ? parsed.message
          : `Request failed (${res.status})`;
    throw new Error(message);
  }

  return parsed;
}

export default function UserNotificationsPopover({ enabled }: { enabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const hasUnread = unreadCount > 0;

  const fetchUnreadCount = useCallback(async () => {
    if (!enabled) return;
    const data = await fetchJson('/api/proxy/v1/notifications/in-app/unread-count');
    setUnreadCount(Number(data?.unread ?? 0));
  }, [enabled]);

  const fetchNotifications = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchJson('/api/proxy/v1/notifications/in-app?unread=true');
      setNotifications(coerceNotifications(data));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load notifications');
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  const markAllRead = useCallback(async () => {
    if (!enabled) return;
    try {
      await fetchJson('/api/proxy/v1/notifications/in-app/read-all', { method: 'POST' });
      setUnreadCount(0);
      setNotifications([]);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to mark notifications as read');
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    // Keep the header lightweight: only fetch counts on mount; fetch details on open.
    fetchUnreadCount().catch(() => {});
  }, [enabled, fetchUnreadCount]);

  useEffect(() => {
    if (!open) return;
    // Refresh both when the user opens the bell.
    fetchUnreadCount().catch(() => {});
    fetchNotifications().catch(() => {});
  }, [open, fetchNotifications, fetchUnreadCount]);

  const content = useMemo(() => {
    if (!enabled) return null;

    if (isLoading) {
      return (
        <div className="space-y-3 px-4 py-3">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="px-4 py-3">
          <p className="text-sm text-red-600">{error}</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => fetchNotifications().catch(() => {})}
          >
            Retry
          </Button>
        </div>
      );
    }

    if (notifications.length === 0) {
      return (
        <div className="px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-900">No notifications yet</p>
          <p className="mt-1 text-xs text-gray-500">
            We&apos;ll show updates here (payments, approvals, and important account activity).
          </p>
        </div>
      );
    }

    return (
      <ScrollArea
        className="max-h-80 overscroll-contain"
        onTouchMove={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="divide-y">
          {notifications.map((n) => {
            const createdAt = n.created_at ? new Date(n.created_at) : null;
            const timeLabel =
              createdAt && !Number.isNaN(createdAt.getTime())
                ? formatDistanceToNow(createdAt, { addSuffix: true })
                : '';

            return (
              <div key={n.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {n.subject || 'Notification'}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-gray-600">
                      {n.message || ''}
                    </p>
                  </div>
                  {timeLabel ? (
                    <span className="shrink-0 text-[11px] text-gray-500">{timeLabel}</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    );
  }, [enabled, error, fetchNotifications, isLoading, notifications]);

  if (!enabled) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="relative">
          <Image
            src="/assets/svgs/Icons/Notification bell.svg"
            width={38}
            height={38}
            alt="notification"
            className="m-0"
          />
          {hasUnread ? (
            <Badge className="absolute -right-1 -top-1 h-5 min-w-5 justify-center px-1 text-[11px]">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 overscroll-contain"
        align="end"
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">Notifications</p>
          {hasUnread ? (
            <Button variant="ghost" size="sm" onClick={() => markAllRead().catch(() => {})}>
              Mark all read
            </Button>
          ) : null}
        </div>
        {content}
      </PopoverContent>
    </Popover>
  );
}
